import { useEffect, useMemo, useRef } from 'react';
import { useUI } from '@/state/store';
import { useLoader } from '../store';
import { getOrbitEngine } from '../engine';
import { getGlobeEngine } from '../globe/engineHandle';
import { resetSSA, useSSA } from './store';
import { spacecraftModelFor } from '../globe/spacecraftCatalog';
import { trajectoryNeedsRefresh } from './trajectory';
import { visibleKind, glyphModel } from './visuals';
import { useReducedMotion } from 'motion/react';
import { zonesFor, useMonitoring } from './monitoringStore';
import { MonitoringRuntime } from './MonitoringRuntime';
import { forecastCurrent } from './monitoring';

export function SsaRuntime({ parked }: { parked: boolean }) {
  const dataset = useLoader((s) => s.dataset);
  const selectedId = useUI((s) => s.selection.missionId);
  const radiusKm = useSSA((s) => s.radiusKm),
    kinds = useSSA((s) => s.kinds),
    scope = useSSA((s) => s.scope);
  const debris = useSSA((s) => s.debris),
    country = useSSA((s) => s.country),
    regime = useSSA((s) => s.regime);
  const pinnedId = useSSA((s) => s.pinnedId);
  const rockets = useSSA((s) => s.rockets),
    fragments = useSSA((s) => s.fragments),
    style = useSSA((s) => s.style),
    mesh = useSSA((s) => s.mesh),
    tumble = useSSA((s) => s.tumble);
  const follow = useSSA((s) => s.follow);
  const labels = useSSA((s) => s.labels),
    connectionMode = useSSA((s) => s.connectionMode);
  const alertsOnly = useSSA((s) => s.alertsOnly),
    zones = useSSA((s) => s.zones),
    animateMotion = useSSA((s) => s.animateMotion),
    reducedMotion = useReducedMotion();
  const focus = useSSA((s) => s.focusRequest);
  const focused = useRef(-1);
  const names = useMemo(
    () => Object.fromEntries((dataset?.missions ?? []).map((m) => [m.id, m.name])),
    [dataset],
  );
  const objects = useMemo(
    () =>
      Object.fromEntries(
        (dataset?.missions ?? []).flatMap((m) => (m.trackedObject ? [[m.id, m.trackedObject]] : [])),
      ),
    [dataset],
  );
  const visible = useMemo(
    () =>
      (dataset?.missions ?? [])
        .filter((m) => {
          const o = m.trackedObject;
          return (
            o &&
            visibleKind(o.kind, debris, rockets, fragments) &&
            (!country || o.country === country) &&
            (!regime || o.regime === regime)
          );
        })
        .map((m) => m.id),
    [dataset, debris, rockets, fragments, country, regime],
  );
  const allowed = useMemo(() => {
    const visibleSet = new Set(visible);
    return (dataset?.missions ?? [])
      .filter(
        (m) =>
          m.trackedObject &&
          visibleKind(m.trackedObject.kind, kinds === 'all', rockets, fragments) &&
          (scope === 'catalogue' || visibleSet.has(m.id)),
      )
      .map((m) => m.id);
  }, [dataset, kinds, rockets, fragments, scope, visible]);

  useEffect(() => {
    resetSSA();
    useUI.getState().clearSelection();
    useUI.setState({ rightPanelOpen: false, bottomPanel: 'collapsed' });
    return () => {
      getGlobeEngine()?.setTacticalFrame?.(null);
      resetSSA();
    };
  }, []);
  useEffect(() => {
    useLoader.getState().setVisibleMissionIds(visible);
  }, [visible]);
  useEffect(() => {
    useSSA.setState((s) => ({
      zones: zonesFor(selectedId),
      result: null,
      orbit: null,
      pinnedId: s.pendingPin,
      pendingPin: null,
      pairCamera: false,
      analysis: null,
      error: null,
    }));
    getGlobeEngine()?.setTacticalFrame?.(null);
    focused.current = -1;
  }, [selectedId]);
  useEffect(() => {
    if (!selectedId || !dataset || parked) return;
    let stopped = false,
      busy = false;
    const engine = getOrbitEngine();
    const selected = dataset.missions.find((m) => m.id === selectedId);
    const reference = selected?.trackedObject?.kind === 'payload' ? spacecraftModelFor(selected) : undefined;
    const model = reference
      ? {
          url: reference.modelUrl,
          scale: reference.metersPerUnit ?? 1,
          span: reference.spanM ?? 10,
          wire: style === 'wire',
        }
      : style !== 'dots' && selected?.trackedObject
        ? { url: glyphModel(selected.trackedObject.kind, style), scale: 1, span: 8 }
        : undefined;
    const unsubscribe = engine.onFrame(async (timestamp) => {
      if (stopped || busy || useLoader.getState().network !== 'full_catalogue') return;
      busy = true;
      try {
        const result = await engine.neighbourhood({
          selectedId,
          radiusKm,
          timestamp,
          allowedIds: allowed,
          pinnedId: pinnedId ?? undefined,
        });
        if (stopped || useUI.getState().selection.missionId !== selectedId) return;
        let orbit = useSSA.getState().orbit;
        if (trajectoryNeedsRefresh(orbit, result.timestamp))
          orbit = await engine.trajectory({ id: selectedId, timestamp: result.timestamp });
        if (stopped || useUI.getState().selection.missionId !== selectedId) return;
        useSSA.setState({ result, orbit });
        const focusNow = focused.current !== focus;
        const forecast = useMonitoring.getState().forecast;
        const predictedIds = forecastCurrent(
          forecast,
          selectedId,
          useMonitoring.getState().revision,
          dataset.catalogueSnapshot?.revision,
          result.timestamp,
        )
          ? forecast.encounters
              .filter((e) => e.closest.t >= result.timestamp && e.closest.rangeKm <= zones.close)
              .map((e) => e.id)
          : [];
        getGlobeEngine()?.setTacticalFrame?.({
          result,
          orbit,
          radiusKm,
          pinnedId,
          names,
          objects,
          mesh,
          tumble,
          labels,
          alertsOnly,
          zones,
          animateMotion: animateMotion && !reducedMotion,
          predictedIds,
          connectionMode,
          analysis: useSSA.getState().analysis,
          pairCamera: useSSA.getState().pairCamera,
          follow,
          focus: focusNow,
          model,
        });
        if (result.originEcfKm) focused.current = focus;
      } catch (error) {
        if (!stopped)
          useSSA.setState({ error: error instanceof Error ? error.message : 'Orbital analysis unavailable' });
      } finally {
        busy = false;
      }
    });
    return () => {
      stopped = true;
      unsubscribe();
    };
  }, [
    selectedId,
    dataset,
    parked,
    radiusKm,
    allowed,
    pinnedId,
    names,
    objects,
    focus,
    follow,
    style,
    mesh,
    tumble,
    labels,
    alertsOnly,
    zones,
    animateMotion,
    reducedMotion,
    connectionMode,
  ]);
  return <MonitoringRuntime parked={parked} />;
}
